<script setup lang="ts">
import "@cisco-u/reicon-lock/cu-reicon-lock.js";

import { debounce } from "lodash-es";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

import AnchorTargetBlankCallback from "@/components/AnchorTargetBlankCallback.vue";
import theCard from "@/components/theCard.vue";
import theProductCard from "@/components/theProductCard.vue";
import isUnauth from "@/utils/isUnauth";
import { toggleMyListItem } from "@/utils/myListActions";
import telemetry from "@/utils/telemetry";

const props = withDefaults(
  defineProps<{
    result?: any;
    index?: number;
    status?: any;
    forCarousel?: boolean;
    cardView?: string;
    cardStyle?: string;
    priceView?: string;
    topicView?: boolean;
    isStoreContent?: boolean;
    showDescription?: boolean;
  }>(),
  {
    result: () => ({}),
    index: 0,
    status: null,
    forCarousel: false,
    cardView: "Grid",
    cardStyle: "Grid",
    priceView: "USD",
    topicView: false,
    isStoreContent: false,
    showDescription: true,
  }
);

const router = useRouter();
const route = useRoute();
const store = useStore();

const click = ref(false);

const inMyList = computed(() => store.getters["user/inMyList"]);
const freeId = computed(() => store.getters["content/freeId"]);

const freeContent = computed((): boolean => {
  return props.result?.content_bundle?.includes(freeId.value);
});

const showPremium = computed((): boolean => {
  if (globalThis.env.ENABLE_LOCK_CONTENT) {
    return Boolean(props.result?.lock_content);
  }
  return !props.status?.userEntitlements?.includes(props.result.guid);
});

const resultLink = computed(() => {
  const iltId = props.result?.friendly_url || props.result?.id;

  return iltId ? router.resolve({ name: "ILT", params: { iltId } }).href : "";
});

const subLink = computed(() => {
  return globalThis.env.LEARNING_LOCATOR_URL + props.result.product_id;
});

const resourceUrl = computed(() => {
  return props.result.catalog_data
    ? props.result.catalog_data.url
    : props.result.url;
});

const hideClicked = () => {
  click.value = false;
};

const whereTo = () => {
  if (showPremium.value) {
    click.value = true;
  } else {
    click.value = false;
    sendTelemetry();
  }
};

const bookmarkMe = (content: any) => {
  toggleMyListItem(content, content.type, {
    inMyList: inMyList.value,
    addContentToMyList: (payload: any) =>
      store.dispatch("user/addContentToMyList", payload),
    removeContentFromMyList: (payload: any) =>
      store.dispatch("user/removeContentFromMyList", payload),
    route,
  });
};

const debouncedBookmarkMe = debounce(
  (content: any) => bookmarkMe(content),
  500
);

const sendTelemetry = async () => {
  let telemetryResponse = telemetry.external(
    {
      action: "launch-contentType",
      url: click.value ? subLink.value : resultLink.value,
      title: click.value ? "Subscribe to Cisco U." : props.result.title,
      id: props.result.id,
      page: route.fullPath,
      type: click.value ? "subscription offer" : props.result.type,
      relevance: props.result.relevance,
      sortOrder: props.index,
    },
    isUnauth()
  );
  telemetryResponse
    .then(() => {
      if (showPremium.value) {
        globalThis.open(subLink.value, "_blank", "noopener");
      } else {
        router.push(resultLink.value);
      }
    })
    .catch(() => {
      // do nothing
    });
};

defineExpose({
  click,
  freeContent,
  showPremium,
  resultLink,
  subLink,
  resourceUrl,
  hideClicked,
  whereTo,
  bookmarkMe,
  debouncedBookmarkMe,
  sendTelemetry,
  removeContentFromMyList: (payload: any) =>
    store.dispatch("user/removeContentFromMyList", payload),
  addContentToMyList: (payload: any) =>
    store.dispatch("user/addContentToMyList", payload),
  result: computed(() => props.result),
  index: computed(() => props.index),
  status: computed(() => props.status),
});
</script>

<template>
  <div
    v-show="result"
    data-cy="instructor-led-result"
    :class="[
      'relative z-10 h-full hover:z-[700] focus:z-[700] active:z-[700]',
      {
        'w-[19rem]': props.cardView === 'Grid' && !topicView,
        'w-full': props.cardView !== 'Grid' && !topicView,
        'w-[22rem]': topicView,
      },
    ]"
  >
    <the-card
      v-if="!isStoreContent"
      :is-unauth="isUnauth()"
      :for-carousel="forCarousel"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :result="result"
      :status="status"
      :cy="'instructor-led-result-link'"
      :link="resultLink"
      :show-description="props.showDescription"
      @navigate="whereTo"
      @bookmark="debouncedBookmarkMe(result)"
      @click.prevent="false"
    />
    <the-product-card
      v-else
      :is-unauth="isUnauth()"
      :card-view="props.cardView"
      :card-style="props.cardStyle"
      :price-view="priceView"
      :result="result"
      :status="status"
      :cy="'instructor-led-result-link'"
      :link="resultLink"
      :show-description="props.showDescription"
      @navigate="whereTo"
      @bookmark="debouncedBookmarkMe(result)"
      @click.prevent="false"
    />
    <div
      :class="[
        'mt-0',
        {
          hidden: !showPremium || !click,
          'mask_it absolute left-0 top-0 mt-0 h-full w-full rounded-[0.625rem] bg-[#000000]/80':
            showPremium && click,
        },
      ]"
      v-if="click"
    >
      <cu-reicon-lock
        type="filled-keyhole"
        dark="true"
        size="sm"
        class="mt-8 flex justify-center"
      />
      <div
        class="mx-10 text-center text-13 text-white-lightest"
        v-click-outside="{
          handler: hideClicked,
          events: ['dblclick', 'click'],
          capture: true,
        }"
      >
        To enroll in this course, use<br />

        <AnchorTargetBlankCallback
          :url="subLink"
          color="white"
          :noUnderline="true"
          class="text-white text-13 font-light underline hover:no-underline"
          @clicked="sendTelemetry"
          >Cisco Learning Locator</AnchorTargetBlankCallback
        >
        to find it for purchase.
      </div>
    </div>
  </div>
</template>
